import type { Column } from '@tanstack/react-table';
import { ListFilter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/Popover';
import { cn } from '@/utils/style';
import { Button } from '../Button';
import { ColumnFilterDropdown } from './ColumnFilterDropdown';

interface ColumnHeaderFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
  data: TData[];
}

export function ColumnHeaderFilter<TData, TValue>({
  column,
  data,
}: ColumnHeaderFilterProps<TData, TValue>) {
  const meta = column.columnDef.meta;
  const filterType = meta?.filterType ?? 'none';

  if (filterType === 'none') {
    return null;
  }

  const filterValue = column.getFilterValue();
  const isFiltered = (() => {
    if (filterValue === undefined) return false;
    if (Array.isArray(filterValue)) return filterValue.length > 0;
    if (typeof filterValue === 'object' && filterValue !== null) {
      const rangeValue = filterValue as { min?: unknown; max?: unknown };
      return rangeValue.min != null || rangeValue.max != null;
    }
    return true;
  })();
  const isSorted = column.getIsSorted() !== false;
  const isActive = isFiltered || isSorted;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={isActive ? 'color' : 'default'}
          className={cn('p-1 ml-1', isActive && 'text-white bg-pink-600 hover:bg-pink-700')}
        >
          <ListFilter className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <ColumnFilterDropdown column={column} data={data} />
      </PopoverContent>
    </Popover>
  );
}
